/** One WebSocket per tab with reconnect; subscriptions are replayed after a reconnect. */
export type WsMessage = { type: string } & Record<string, unknown>;
type Listener = (m: WsMessage) => void;

class Realtime {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private subs = new Set<string>();
  private attempt = 0;
  private timer: number | null = null;
  state: 'offline' | 'connecting' | 'live' = 'offline';
  private stateListeners = new Set<(s: Realtime['state']) => void>();
  private editing: string | null = null;
  /** The bulletins whose autocue this tab shows or follows, joined again after a reconnect. */
  private autocue = new Map<string, 'screen' | 'watch'>();

  onState(cb: (s: Realtime['state']) => void): () => void {
    this.stateListeners.add(cb);
    cb(this.state);
    return () => this.stateListeners.delete(cb);
  }

  private setState(s: Realtime['state']) {
    this.state = s;
    for (const cb of this.stateListeners) cb(s);
  }

  connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    this.setState('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.socket = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.setState('live');
      for (const r of this.subs) ws.send(JSON.stringify({ type: 'subscribe', rundownId: r }));
      if (this.editing) ws.send(JSON.stringify({ type: 'editing', storyId: this.editing }));
      for (const [r, as] of this.autocue) ws.send(JSON.stringify({ type: 'autocue-join', rundownId: r, as }));
    };
    ws.onmessage = (ev) => {
      let m: WsMessage;
      try {
        m = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      for (const l of this.listeners) l(m);
    };
    ws.onclose = (ev) => {
      this.socket = null;
      this.setState('offline');
      if (ev.code === 4401) return; // not signed in: the session screen takes over
      const delay = Math.min(15_000, 500 * 2 ** this.attempt++);
      this.timer = window.setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => ws.close();
  }

  disconnect(): void {
    if (this.timer) window.clearTimeout(this.timer);
    this.socket?.close();
    this.socket = null;
    this.setState('offline');
  }

  listen(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  subscribe(rundownId: string): () => void {
    this.subs.add(rundownId);
    this.send({ type: 'subscribe', rundownId });
    return () => {
      this.subs.delete(rundownId);
      this.send({ type: 'unsubscribe', rundownId });
    };
  }

  /** Show (or, the Control Room, follow) a bulletin's autocue: its screens move together. */
  joinAutocue(rundownId: string, as: 'screen' | 'watch'): () => void {
    this.autocue.set(rundownId, as);
    this.send({ type: 'autocue-join', rundownId, as });
    return () => {
      this.autocue.delete(rundownId);
      this.send({ type: 'autocue-leave', rundownId });
    };
  }

  /** This screen of the autocue moved, by a hand or by its voice: the others follow (the server lets only those who may drive the autocue). */
  autocueAt(rundownId: string, itemId: string, frac: number, by: 'hand' | 'voice'): void {
    this.send({ type: 'autocue-at', rundownId, itemId, frac, by });
  }

  setEditing(storyId: string | null): void {
    this.editing = storyId;
    this.send({ type: 'editing', storyId });
  }

  private send(m: unknown) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(m));
  }
}

export const realtime = new Realtime();
